import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { getSupabaseBrowserClient } from '../../../lib/supabase/client'
import { useAuth } from '../../auth/providers/AuthProvider'
import { onboardingModes, saveOnboardingMode, type OnboardingMode } from '../../onboarding/lib/entry'
import { buildLocalOrganizationSummary } from '../lib/mappers'
import type { OrganizationSummary } from '../lib/types'

type OrganizationContextValue = {
  isReady: boolean
  organization: OrganizationSummary | null
  refresh: () => Promise<void>
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null)

async function fetchOrganizationSummary(userId: string) {
  const supabase = getSupabaseBrowserClient()
  if (!supabase) {
    return null
  }

  const { data: membership } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', userId)
    .eq('status', 'active')
    .limit(1)
    .maybeSingle()

  let organizationId = membership?.organization_id ?? null

  if (!organizationId) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('last_organization_id')
      .eq('id', userId)
      .maybeSingle()

    organizationId = profile?.last_organization_id ?? null
  }

  if (!organizationId) {
    return null
  }

  const [{ data: organization }, { data: brandProfile }, { data: toneProfile }, { data: organizationSettings }] = await Promise.all([
    supabase
      .from('organizations')
      .select('id, name, slug, industry, employee_range, city, postal_code')
      .eq('id', organizationId)
      .maybeSingle(),
    supabase
      .from('brand_profiles')
      .select(
        'website_url, vision_statement, goals, audience_summary, decision_makers, trigger_context, markenkern, positioning, mission, usp, primary_offer, strategic_goals, proof_points, taboo_words, preferred_phrases, channel_linkedin_connected, linkedin_page_name, channel_instagram_connected, instagram_handle, channel_newsletter_connected, newsletter_sender_email, newsletter_audience_source',
      )
      .eq('organization_id', organizationId)
      .maybeSingle(),
    supabase
      .from('brand_tone_profiles')
      .select('formality, emotionality, technicality, assertiveness, verbosity')
      .eq('organization_id', organizationId)
      .eq('channel', 'base')
      .maybeSingle(),
    supabase
      .from('organization_settings')
      .select('onboarding_completed_steps, onboarding_completed_at, onboarding_mode')
      .eq('organization_id', organizationId)
      .maybeSingle(),
  ])

  if (!organization) {
    return null
  }

  const localFallback = buildLocalOrganizationSummary()
  const onboardingModeRaw = organizationSettings?.onboarding_mode
  const onboardingMode = onboardingModes.includes(onboardingModeRaw as OnboardingMode)
    ? (onboardingModeRaw as OnboardingMode)
    : 'guided'

  return {
    id: organization.id,
    name: organization.name,
    slug: organization.slug,
    industry: organization.industry,
    employeeRange: (organization.employee_range as string | null) ?? null,
    city: (organization.city as string | null) ?? null,
    postalCode: (organization.postal_code as string | null) ?? null,
    websiteUrl: brandProfile?.website_url ?? localFallback.websiteUrl,
    visionStatement: brandProfile?.vision_statement ?? localFallback.visionStatement,
    goals: (brandProfile?.goals as string[] | null) ?? localFallback.goals,
    markenkern: (brandProfile?.markenkern as string | null) ?? localFallback.markenkern,
    positioning: (brandProfile?.positioning as string | null) ?? localFallback.positioning,
    mission: (brandProfile?.mission as string | null) ?? localFallback.mission,
    usp: (brandProfile?.usp as string | null) ?? localFallback.usp,
    primaryOffer: (brandProfile?.primary_offer as string | null) ?? localFallback.primaryOffer,
    strategicGoals: (brandProfile?.strategic_goals as string[] | null) ?? localFallback.strategicGoals,
    proofPoints: (brandProfile?.proof_points as string[] | null) ?? localFallback.proofPoints,
    audienceSummary: brandProfile?.audience_summary ?? localFallback.audienceSummary,
    decisionMakers: (brandProfile?.decision_makers as string[] | null) ?? localFallback.decisionMakers,
    triggerContext: brandProfile?.trigger_context ?? localFallback.triggerContext,
    tone: {
      formality: toneProfile?.formality ?? localFallback.tone.formality,
      emotionality: toneProfile?.emotionality ?? localFallback.tone.emotionality,
      technicality: toneProfile?.technicality ?? localFallback.tone.technicality,
      assertiveness: toneProfile?.assertiveness ?? localFallback.tone.assertiveness,
      verbosity: toneProfile?.verbosity ?? localFallback.tone.verbosity,
    },
    tabooWords: (brandProfile?.taboo_words as string[] | null) ?? localFallback.tabooWords,
    preferredPhrases: (brandProfile?.preferred_phrases as string[] | null) ?? localFallback.preferredPhrases,
    onboardingCompletedSteps: Array.isArray(organizationSettings?.onboarding_completed_steps)
      ? (organizationSettings.onboarding_completed_steps as string[])
      : localFallback.onboardingCompletedSteps,
    onboardingCompletedAt: (organizationSettings?.onboarding_completed_at as string | null) ?? localFallback.onboardingCompletedAt,
    onboardingMode,
    channelLinkedinConnected: (brandProfile?.channel_linkedin_connected as boolean | null) ?? false,
    linkedinPageName: (brandProfile?.linkedin_page_name as string | null) ?? '',
    channelInstagramConnected: (brandProfile?.channel_instagram_connected as boolean | null) ?? false,
    instagramHandle: (brandProfile?.instagram_handle as string | null) ?? '',
    channelNewsletterConnected: (brandProfile?.channel_newsletter_connected as boolean | null) ?? false,
    newsletterSenderEmail: (brandProfile?.newsletter_sender_email as string | null) ?? '',
    newsletterAudienceSource: (brandProfile?.newsletter_audience_source as string | null) ?? 'Bestandskundenliste',
    source: 'supabase',
  } satisfies OrganizationSummary
}

export function OrganizationProvider({ children }: Readonly<PropsWithChildren>) {
  const { isAuthenticated, isReady, user } = useAuth()
  const [organization, setOrganization] = useState<OrganizationSummary | null>(null)
  const [organizationReady, setOrganizationReady] = useState(false)

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setOrganization(null)
      setOrganizationReady(true)
      return
    }

    setOrganizationReady(false)

    const summary = await fetchOrganizationSummary(user.id)
    if (summary?.onboardingMode) {
      saveOnboardingMode(summary.onboardingMode)
    }
    setOrganization(summary)
    setOrganizationReady(true)
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isReady) {
      return
    }

    void refresh()
  }, [isReady, refresh])

  const value = useMemo<OrganizationContextValue>(() => {
    return {
      isReady: organizationReady,
      organization,
      refresh,
    }
  }, [organization, organizationReady, refresh])

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
}

export function useOrganization() {
  const context = useContext(OrganizationContext)

  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }

  return context
}